import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { PageHeader } from '@/shared/ui/PageHeader'
import { Card } from '@/shared/ui/Card'
import { Spinner } from '@/shared/ui/Spinner'
import { formatCurrency } from '@/shared/utils'
import { useRefundDetails } from '@/api/hooks'
import { DollarSign, Receipt, Percent, CalendarDays } from 'lucide-react'
import type { ReactNode } from 'react'
import type { RefundDetails } from '@/shared/types'

function SummaryItem({ icon, label, value, color = 'text-gray-900' }: { icon?: ReactNode; label: string; value: ReactNode; color?: string }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1.5 text-gray-500">
        {icon}
        <span className="text-xs">{label}</span>
      </div>
      <span className={`text-base font-semibold ${color}`}>{value}</span>
    </div>
  )
}

export function RefundDetailsPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const base = location.pathname.startsWith('/school') ? '/school' : '/dashboard'

  const { data, isLoading, isError } = useRefundDetails(Number(id))
  const d: RefundDetails = (data as any)?.data ?? {}

  if (isLoading) {
    return <div className="flex justify-center py-20"><Spinner /></div>
  }
  if (isError) {
    return (
      <div className="flex flex-col items-center py-20 text-center">
        <p className="text-sm font-semibold text-red-500">{t('refundPage.details.loadFailed')}</p>
        <button onClick={() => navigate(-1)} className="mt-3 text-xs text-brand-600 hover:underline">{t('refundPage.details.goBack')}</button>
      </div>
    )
  }

  const money = (v?: number) => formatCurrency(Number(v ?? 0))

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('refundPage.details.title')}
        breadcrumbs={[{ label: t('refundPage.breadcrumb.dashboard'), to: base }, { label: t('refundPage.breadcrumb.refund'), to: `${base}/booking/refund` }, { label: t('refundPage.details.bookingBreadcrumb', { id }) }]}
      />

      <Card>
        <div className="grid grid-cols-2 gap-6">
          {/* Key Amounts */}
          <SummaryItem icon={<DollarSign size={14} className="text-green-500" />} label={t('refundPage.details.totalPaidByStudent')} value={money(d.total_paid_amount_by_student)} color="text-green-600" />
          <SummaryItem icon={<DollarSign size={14} className="text-brand-500" />} label={t('refundPage.details.refundableAmount')} value={money(d.refundable_amount)} color="text-brand-600" />
        </div>

        <div className="my-5 border-t border-gray-100" />

        {/* Fees */}
        <div className="grid grid-cols-2 gap-6">
          <SummaryItem icon={<Receipt size={14} className="text-brand-500" />} label={t('refundPage.details.cancellationFee')} value={money(d.cancellation_fee)} />
          <SummaryItem icon={<Receipt size={14} className="text-brand-500" />} label={t('refundPage.details.cashServiceFee')} value={money(d.cash_service_fee)} />
          <SummaryItem icon={<Percent size={14} className="text-purple-500" />} label={t('refundPage.details.taxAmount')} value={money(d.tax_amount)} />
        </div>

        <div className="my-5 border-t border-gray-100" />

        {/* Commissions */}
        <div className="grid grid-cols-2 gap-6">
          <SummaryItem label={t('refundPage.details.drivysCommission')} value={money(d.drivys_commission)} />
          <SummaryItem label={t('refundPage.details.vendorCommission')} value={money(d.vendor_commission)} />
          <SummaryItem label={t('refundPage.details.trainerCommission')} value={money(d.trainer_commission)} />
          <SummaryItem label={t('refundPage.details.totalCommissions')} value={money(d.total_commissions)} color="text-brand-600" />
        </div>

        <div className="my-5 border-t border-gray-100" />

        {/* Sessions */}
        <div className="grid grid-cols-3 gap-6">
          <SummaryItem icon={<CalendarDays size={14} className="text-brand-500" />} label={t('refundPage.details.numberOfSessions')} value={d.number_of_session ?? 0} />
          <SummaryItem label={t('refundPage.details.completedSessions')} value={d.completed_session ?? 0} color="text-green-600" />
          <SummaryItem label={t('refundPage.details.cancelledSessions')} value={d.cancelled_session ?? 0} color="text-red-500" />
        </div>
      </Card>
    </div>
  )
}
